import { memo, useMemo, lazy, Suspense } from 'react';
import {
	generateVariant,
	floatingLinesVariants,
	gradientAnimVariants,
	grainientVariants,
	liquidChromeVariants,
	lightPillarVariants,
} from '../../animated-backgrounds/constants.js';
import { getAnimationVariant, floikColour, hexToRgb } from '../../Common/Common.utils';
const LazyFloatingLines = lazy(() => import('../../animated-backgrounds/floatingLines'));
const LazyBackgroundGradientAnimation = lazy(
	() => import('../../animated-backgrounds/gradientBg')
);
const LazyGrainient = lazy(() => import('../../animated-backgrounds/grainient'));
const LazyLightPillar = lazy(() => import('../../animated-backgrounds/lightPillar'));
const LazyLiquidChrome = lazy(() => import('../../animated-backgrounds/liquidChrome'));

interface BackgroundProps {
	children: React.ReactNode;
	theme?: any;
	show?: boolean;
	animatedThemeColor?: any;
	animation?: any;
}

const Background = memo(
	function Background({
		children,
		animatedThemeColor = 'none',
		animation,
	}: BackgroundProps) {
		// Compute theme values unconditionally so that hooks are always called
		// in the same order on every render. The animated component itself is
		// only rendered when animation is set and the page is visible.
		const variant = animation ? getAnimationVariant(animatedThemeColor) : null;

		let themeColor: string | null = null;
		if (variant === null || variant === undefined) {
			if (animatedThemeColor?.startsWith('brand_')) {
				const extractedColor = animatedThemeColor.substring(6);
				themeColor = extractedColor.startsWith('#')
					? extractedColor
					: `#${extractedColor}`;
			} else if (animatedThemeColor?.startsWith('custom_')) {
				const extractedColor = animatedThemeColor.substring(7);
				themeColor = extractedColor.startsWith('#')
					? extractedColor
					: `#${extractedColor}`;
			} else {
				themeColor = floikColour;
			}
		}

		const animationComponent = useMemo(() => {
			if (!animation) return null;

			switch (animation) {
				case 'floating-lines': {
					console.log('theme[log]', themeColor);
					const animationConstant =
						variant !== null
							? floatingLinesVariants[variant]
							: generateVariant(themeColor!, 'floating-lines');
					return (
						<LazyFloatingLines
							linesGradient={animationConstant.gradient}
							enabledWaves={['top', 'middle', 'bottom']}
							lineCount={[6, 8, 6]}
							lineDistance={[5, 3, 5]}
							animationSpeed={1}
							interactive={false}
							mixBlendMode="screen"
						/>
					);
				}

				case 'gradient':
				case 'gradient-animation': {
					const animationConstant =
						variant !== null
							? gradientAnimVariants[variant]
							: generateVariant(themeColor!, 'gradient');
					return (
						<LazyBackgroundGradientAnimation
							gradientBackgroundStart={animationConstant.bgStart}
							gradientBackgroundEnd={animationConstant.bgEnd}
							firstColor={animationConstant.first}
							secondColor={animationConstant.second}
							thirdColor={animationConstant.third}
							fourthColor={animationConstant.fourth}
							fifthColor={animationConstant.fifth}
							pointerColor={animationConstant.pointer}
							interactive={false}
						/>
					);
				}

				case 'grain-effect':
				case 'grainient': {
					const animationConstant =
						variant !== null
							? grainientVariants[variant]
							: generateVariant(themeColor!, 'grain-effect');
					return (
						<LazyGrainient
							color1={animationConstant.color1}
							color2={animationConstant.color2}
							color3={animationConstant.color3}
							timeSpeed={1.15}
							colorBalance={0}
							warpStrength={1}
							warpFrequency={5}
							warpSpeed={3.4}
							warpAmplitude={50}
							blendAngle={0}
							blendSoftness={0.05}
							rotationAmount={500}
							noiseScale={2}
							grainAmount={0.1}
							grainScale={2}
							grainAnimated={false}
							contrast={1.5}
							gamma={1}
							saturation={1}
							centerX={0}
							centerY={0.13}
							zoom={0.9}
						/>
					);
				}

				case 'light-pillar': {
					const animationConstant =
						variant !== null
							? lightPillarVariants[variant]
							: generateVariant(themeColor!, 'light-pillar');
					return (
						<LazyLightPillar
							topColor={animationConstant.topColor}
							bottomColor={animationConstant.bottomColor}
							intensity={1.0}
							rotationSpeed={0.3}
							glowAmount={0.006}
							pillarWidth={3.96}
							pillarHeight={0.528}
							noiseIntensity={0.5}
							pillarRotation={45}
							quality="high"
						/>
					);
				}

				case 'liquid-chrome': {
					const animationConstant =
						variant !== null
							? liquidChromeVariants[variant]
							: generateVariant(themeColor!, 'liquid-chrome');
					return (
						<LazyLiquidChrome
							baseColor={animationConstant}
							speed={0.2}
							amplitude={0.3}
							frequencyX={3}
							frequencyY={3}
						/>
					);
				}

				default: {
					const rgbColor = hexToRgb(floikColour);
					return (
						<div
							style={{
								position: 'absolute',
								width: '100%',
								height: '100%',
								backgroundColor: rgbColor
									? `rgba(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}, 0.5)`
									: floikColour,
							}}
						></div>
					);
				}
			}
		}, [animation, variant, themeColor]);

		if (!animation) {
			return <>{children}</>;
		}

		return (
			<>
				<div
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						zIndex: 0,
						pointerEvents: 'none',
						willChange: 'transform',
					}}
				>
					<Suspense fallback={null}>{animationComponent}</Suspense>
				</div>
				<div style={{ position: 'relative', zIndex: 1, height: '100%', width: '100%' }}>
					{children}
				</div>
			</>
		);
	},
	(prevProps, nextProps) => {
		// Only re-render if theme or animation changes
		return (
			prevProps.animatedThemeColor === nextProps.animatedThemeColor &&
			prevProps.animation === nextProps.animation &&
			prevProps.theme === nextProps.theme
		);
	}
);

export default Background;
